import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrefs, useLabels } from 'ws-scripts/modules/common';
import { setClassNames } from '@ddc/classnames-js';
import ReadMore from './ReadMore';
import ReviewHeader from './ReviewHeader';
import './Reviews.scss';

const Reviews = () => {
	const kbbWidgetInfo = useSelector((state) => state.widgetInfo);
	const { items: reviews } = useSelector((state) => state.reviews);
	const prefs = usePrefs();

	const [useFullWidthLayout, setUseFullWidthLayout] = useState(false);

	useEffect(() => {
		const kbbContainer = document.querySelector(
			'[data-widget-name="ws-kbb-reviews"]'
		);

		if (kbbContainer?.clientWidth >= 992) {
			setUseFullWidthLayout(true);
		}
	}, []);

	const labels = useLabels();
	const { windowId, widgetName } = kbbWidgetInfo;
	const {
		reviewHeadingWeight,
		reviewHeadingCase,
		reviewRatingFontSize,
		useHorizontalStyle,
		useInverseTextColor,
		circleRatingBgColor
	} = prefs;
	if (!reviews) {
		return null;
	}

	const textColor = useInverseTextColor === 'true' ? 'text-inverse' : '';
	const textMutedColor =
		useInverseTextColor === 'true' ? 'text-muted-inverse' : 'text-muted';

	const fullWidthLayoutClasses = [
		'list-unstyled',
		'd-flex flex-wrap',
		'align-items-start',
		useFullWidthLayout ? 'row' : ''
	];

	// Change Bootstrap classes according to the number of reviews
	let columnClass = '';
	switch (reviews.length) {
		case 2:
			columnClass = 'col-md-6';
			break;
		case 3:
			columnClass = 'col-md-4';
			break;
		default:
			columnClass = '';
			break;
	}

	const reviewItemClasses = [
		'review-wrapper',
		'flex-column',
		'py-4',
		'd-flex',
		useFullWidthLayout ? `col-xs-12 ${columnClass}` : ''
	];

	const reviewHeaderClasses = [
		'kbb-review-item',
		'flex-grow-1',
		'd-flex',
		'align-items-center',
		'mb-3',
		'pl-3'
	];

	// when new UI is used
	if (useHorizontalStyle === 'true') {
		return (
			<div className="kbb-reviews mt-3">
				<ul className={setClassNames(fullWidthLayoutClasses)}>
					{reviews.map((review) => (
						<li
							key={review.reviewId}
							className={setClassNames(reviewItemClasses)}
						>
							<ReviewHeader 
								review={review}
								reviewHeadingWeight={reviewHeadingWeight}
								reviewHeadingCase={reviewHeadingCase}
								reviewRatingFontSize={reviewRatingFontSize}
								circleRatingBgColor={circleRatingBgColor}
								textColor={textColor}
								textMutedColor={textMutedColor}
								className={setClassNames(reviewHeaderClasses)}
							/>

							<ReadMore
								className="kbb-review-text"
								windowId={windowId}
								widgetName={widgetName}
								labels={labels}
							>
								{review.text}
							</ReadMore>
						</li>
					))}
				</ul>
			</div>
		);
	}

	return (
		<div className="kbb-reviews mt-3">
			<ul className="list-unstyled">
				{reviews.map((review) => (
					<li key={review.reviewId} className="border-bottom py-4">
						<div className="kbb-review-item d-flex">
							<div className="kbb-review-heading-container">
								<h4
									className={`kbb-review-heading ${reviewHeadingWeight} ${reviewHeadingCase} m-0 ${textColor}`}
								>
									{review.title}
								</h4>
								<p className={textMutedColor}>
									{review.nickName && (
										<span>
											<span className="kbb-review-name">
												By {review.nickName}
											</span>
											<span
												className="separator"
												aria-hidden="true"
											>
												&nbsp;|&nbsp;
											</span>
											<span className="sr-only">
												{' '}
												on{' '}
											</span>
										</span>
									)}

									<span className="kbb-review-submission-date">
										{review.submissionDate}
									</span>
								</p>
							</div>
							<strong
								className={`kbb-review-rating ml-auto font-weight-bold ${reviewRatingFontSize} ${textColor}`.trim()}
							>
								{review.overallRating &&
									parseFloat(review.overallRating).toFixed(1)}
							</strong>
						</div>

						<ReadMore
							className="kbb-review-text"
							windowId={windowId}
							widgetName={widgetName}
							labels={labels}
						>
							{review.text}
						</ReadMore>
					</li>
				))}
			</ul>
		</div>
	);
};

export default Reviews;
